import { registerView, View, ViewComponent } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import React, { useRef, useEffect } from "react";
import { Col, Form, Jumbotron, Row } from "react-bootstrap";
import OrderRecapViewModel from "../../viewModels/multiEshop/orderRecapViewModel";
import RecommendationAlert from "./components/recommendationAlert";
import { Tag } from "@edaz/edazkit/TaggedInput";
import { Check } from "@frui.ts/bootstrap";
import Status from "./components/status";
import Button from "@edaz/edazkit/Button";
import { IconType } from "@edaz/edazkit/Icon";
import LoadingPane from "controls/loadingPane";
import ChargeOverlapType from "entities/chargeOverlapType";
import TextWithLink from "views/common/textWithLink/textWithLink";
import { getTimeZoneLabel } from "utils/dateUtils";
import Tooltip from "controls/tooltip";
import { isMobile } from "react-device-detect";

const orderRecapView: ViewComponent<OrderRecapViewModel> = observer(({ vm }) => {
  const rootRef = useRef<HTMLDivElement>(null);

  const renderDateWithTimeZone = (date: Date) => {
    const formatted = vm.localizationService.formatDateTime(date);
    return !vm.isDifferentTimeZone ? (
      <span>{formatted}</span>
    ) : (
      <>
        <span>{formatted}</span>
        <Tooltip
          id="cet-tooltip"
          placement={isMobile ? "bottom" : "right"}
          tooltip={vm.localizationService.translateGeneral("timezoneTooltip")}
          trigger={["hover", "focus"]}>
          <span style={{ marginLeft: 4, textDecoration: "underline", textDecorationStyle: "dotted" }}>
            {getTimeZoneLabel(date)}
          </span>
        </Tooltip>
      </>
    );
  };

  return (
    <div ref={rootRef} id="multiEshop">
      <Row>
        <Col sm={12} lg={8}>
          <Jumbotron className="multi-eshop">
            <div className="p-s-0 px-lg-7">
              <Status current={2} statusSteps={vm.statusNavigation} translate={vm.translate} />
            </div>
            {vm.loading === false ? (
              <>
                {vm.countRecommendation > 0 && (
                  <div className="mb-7 p-s-0 px-lg-7">
                    <Row className="mb-4">
                      <Col sm={6} xl={6} className="d-flex align-items-center justify-content-start mb-3">
                        <h3 className="mb-0">{vm.translate("recommendation")}</h3>
                      </Col>
                      <Col sm={6} xl={6} className="d-flex align-items-center justify-content-end mb-3">
                        <Button
                          type="button"
                          variant="light-danger"
                          size="sm"
                          style={{ textTransform: "none" }}
                          onClick={vm.resetChargeOrder}
                          iconLeftVariant="light-danger"
                          iconLeft={IconType.Cross}>
                          {vm.translate("cancel")}
                        </Button>
                      </Col>
                    </Row>
                    {vm.possibleExemptions?.map((exemption, index) => {
                      return (
                        <RecommendationAlert key={index} vm={vm} disabled={false}>
                          <div>
                            <TextWithLink
                              text={vm
                                .translateFuelTypeLimitation(exemption.fuelTypeLimitation.limitationReason)
                                .replace("%FUEL_TYPE%", exemption.fuelTypeLable)}
                              link={vm.translateFuelTypeLimitationLink(exemption.fuelTypeLimitation.limitationReason)}
                            />
                          </div>
                          {exemption?.vehicles
                            ? exemption.vehicles.map((v, index) => (
                                <div
                                  key={index}
                                  className="d-inline-flex flex-wrap mt-3 mb-2 font-weight-normal text-dark with-gap">
                                  <Tag
                                    key={`${Math.random().toString()}${v}`}
                                    item={{ label: v.licensePlate ?? "", valid: false }}
                                  />
                                </div>
                              ))
                            : null}
                        </RecommendationAlert>
                      );
                    })}
                    {vm.exemptionIdentifiedPossibleLicensePlate?.map((exemption, index) => {
                      return (
                        <RecommendationAlert
                          key={index}
                          vm={vm}
                          descriptionLocalizationCode={`recommendationExemptions.${exemption.instructionType}`}
                          removeButtonLabelLocalizationCode="removeFromOrder"
                          closeButtonLabelLocalizationCode="closeRecommendation"
                          removeAction={
                            exemption?.vehicles?.length ? () => vm.removeIdentifiedPossibleFromOrderItems(exemption) : undefined
                          }
                          closeAction={
                            exemption?.vehicles?.length
                              ? () => vm.hideIdentifiedPossibleRecommendation(exemption.possibleExemptionId)
                              : undefined
                          }
                          disabled={false}>
                          {exemption?.vehicles
                            ? exemption.vehicles.map((v, index) => (
                                <div
                                  key={index}
                                  className="d-inline-flex flex-wrap mt-3 mb-2 font-weight-normal text-dark with-gap">
                                  <Tag
                                    key={`${Math.random().toString()}${v}`}
                                    item={{ label: v.licensePlate ?? "", valid: true }}
                                  />
                                </div>
                              ))
                            : null}
                        </RecommendationAlert>
                      );
                    })}
                    {vm.exemptionLicensePlates?.map((exemption, index) => {
                      return (
                        <RecommendationAlert
                          key={index}
                          vm={vm}
                          descriptionLocalizationCode={`recommendationExemptions.${exemption.instructionType}`}
                          removeButtonLabelLocalizationCode="removeFromOrder"
                          closeButtonLabelLocalizationCode="closeRecommendation"
                          removeAction={
                            exemption?.vehicles?.length ? () => vm.removeExemptionsFromOrderItems(exemption) : undefined
                          }
                          closeAction={
                            exemption?.vehicles?.length
                              ? () => vm.hideExemptionRecommendation(exemption.possibleExemptionId)
                              : undefined
                          }
                          disabled={false}>
                          {exemption?.vehicles
                            ? exemption.vehicles.map((v, index) => (
                                <div
                                  key={index}
                                  className="d-inline-flex flex-wrap mt-3 mb-2 font-weight-normal text-dark with-gap">
                                  <Tag
                                    key={`${Math.random().toString()}${v}`}
                                    item={{ label: v.licensePlate ?? "", valid: true }}
                                  />
                                </div>
                              ))
                            : null}
                        </RecommendationAlert>
                      );
                    })}
                    {vm.overlayExemptionVisible && (
                      <RecommendationAlert
                        vm={vm}
                        descriptionLocalizationCode={`overlapWarnings.${ChargeOverlapType.ExemptionOverlap}`}
                        disabled={false}
                        removeButtonLabelLocalizationCode="removeFromOrder"
                        closeButtonLabelLocalizationCode="closeRecommendation"
                        removeAction={vm.removeExemptionOverlaysFromOrderItems}
                        closeAction={vm.hideExemptionOverlayRecommendation}>
                        {vm.overlapsExemption
                          ? vm.overlapsExemption.map((v, index) => (
                              <div
                                key={index}
                                className="d-inline-flex flex-wrap mt-3 mb-2 font-weight-normal text-dark with-gap">
                                <Tag
                                  key={`${Math.random().toString()}${v}`}
                                  item={{ label: v.vehicle.licensePlate, valid: true }}
                                />
                              </div>
                            ))
                          : null}
                      </RecommendationAlert>
                    )}
                    {vm.overlayRecommendationVisible && (
                      <RecommendationAlert
                        vm={vm}
                        descriptionLocalizationCode={`overlapWarnings.${ChargeOverlapType.ChargeOverlap}`}
                        removeButtonLabelLocalizationCode="removeSelected"
                        closeButtonLabelLocalizationCode="closeRecommendation"
                        removeAction={vm.removeOverlaysFromOrderItems}
                        closeAction={vm.hideOverlayRecommendation}
                        disabled={vm.overlaysRemoveDisabled}>
                        <div className="d-inline-flex flex-wrap mt-2 mb-4 w-100 font-weight-normal text-dark with-gap">
                          <div>
                            <table className="table">
                              <thead>
                                <tr>
                                  <th>
                                    <Check
                                      autoComplete="off"
                                      custom
                                      label=""
                                      target={vm}
                                      threeState={true}
                                      property="allOverlaysSelected"
                                      onValueChanged={vm.selectAllOverlays}
                                    />
                                  </th>
                                  <th>{vm.translate("licensePlate")}</th>
                                  <th className="d-sm-block d-none">{vm.translate("licensePlateState")}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {vm.overlayLicensePlates.map((item, index) => (
                                  <tr key={index}>
                                    <td>
                                      <Check
                                        autoComplete="off"
                                        target={item}
                                        property="selected"
                                        custom
                                        label=""
                                        key={item.licensePlate}
                                        onValueChanged={vm.select}
                                      />
                                    </td>
                                    <td>
                                      <div className="d-inline-flex flex-wrap font-weight-normal text-dark with-gap">
                                        <Tag item={{ label: item.licensePlate, valid: true }} />
                                      </div>
                                      <div className="d-block d-sm-none">
                                        <span className="d-block">{vm.formatOverlayState(item.state)}</span>
                                        <span>{item?.expiration ? renderDateWithTimeZone(item?.expiration) : vm.emptyState}</span>
                                      </div>
                                    </td>
                                    <td className="d-sm-block d-none">
                                      <span className="d-block d-md-inline">{vm.formatOverlayState(item.state)}</span>
                                      <span>{item?.expiration ? renderDateWithTimeZone(item?.expiration) : vm.emptyState}</span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </RecommendationAlert>
                    )}
                  </div>
                )}
                <div className="p-s-0 px-lg-7">
                  <Form.Row className="d-flex flex-row flex-wrap align-items-center justify-content-between mx-0 my-0 mb-7">
                    <h3 className="mb-0">{vm.translate("recap")}</h3>
                    {vm.countRecommendation === 0 && (
                      <Button
                        type="button"
                        variant="light-danger"
                        size="sm"
                        style={{ textTransform: "none" }}
                        onClick={vm.resetChargeOrder}
                        iconLeftVariant="light-danger"
                        iconLeft={IconType.Cross}>
                        {vm.translate("cancel")}
                      </Button>
                    )}
                  </Form.Row>
                </div>
                <div className="table-container">
                  <div className="table-header d-lg-flex d-none">
                    <div className="table-cell without-text"></div>
                    <div className="table-cell without-text">
                      <div>{vm.translate("pieces")}</div>
                    </div>
                    <div className="table-cell">
                      <div>{vm.translate("type")}</div>
                    </div>
                    <div className="table-cell">
                      <div>{vm.translate("fuelType")}</div>
                    </div>
                    <div className="table-cell">
                      <div>{vm.translate("price")}</div>
                    </div>
                    <div className="table-cell">
                      <div>{vm.translate("validityPeriod")}</div>
                    </div>
                    <div className="table-cell without-text"></div>
                  </div>

                  {vm.chargeOrderCharges
                    .filter(item => item.licensePlates.length && item.fuelTypeId)
                    .map((item, index) => {
                      const showDetail = vm.detail === item && vm.detailIndex === index;
                      return (
                        <div key={index} className={`table-body ${index > 0 ? "mt-2" : ""}`}>
                          <div className={`table-row align-items-start align-items-lg-center ${showDetail ? "detail-row" : ""}`}>
                            <div className="table-cell font-weight-bold without-text">{`${index + 1}.`}</div>
                            <div className="table-cell without-text">
                              <strong className="mr-2 d-lg-none">{vm.translate("pieces")}</strong>
                              {vm.translate("chargeCount").replace("%charge%", item.licensePlates.length.toString())}
                            </div>
                            <div className="table-cell">
                              <strong className="mr-2 d-lg-none">{vm.translate("type")}</strong>
                              {vm.translateChargeLabel(item.chargeTypeId as string)}
                            </div>
                            <div className="table-cell">
                              <strong className="mr-2 d-lg-none">{vm.translate("fuelType")}</strong>
                              {vm.translateFuelLabel(item.fuelTypeId as string)}
                            </div>
                            <div className="table-cell">
                              <strong className="mr-2 d-lg-none">{vm.translate("price")}</strong>
                              {vm.formatPrice(vm.chargePrice(item))}
                            </div>
                            <div className="table-cell d-flex flex-column">
                              <strong className="mr-2 d-lg-none">{vm.translate("validityPeriod")}</strong>
                              <span>{item.validSince ? renderDateWithTimeZone(new Date(item.validSince)) : vm.emptyState}</span>
                              <span>{item._validUntil ? renderDateWithTimeZone(new Date(item._validUntil)) : vm.emptyState}</span>
                            </div>
                            <div className="table-cell d-flex justify-content-end without-text">
                              <Button
                                onClick={() => vm.showDetail(item, index)}
                                variant="light"
                                size="sm"
                                iconRight={showDetail ? IconType.ArrowTop : IconType.ArrowBottom}
                              />
                            </div>
                          </div>
                          {showDetail && (
                            <>
                              <div key={index} className={`table-row ${showDetail ? "license-detail-row" : ""}`}>
                                <div className="table-cell license-plate-row">
                                  {item.licensePlates.map((license, index) => {
                                    return (
                                      <span key={index} className="mr-2 mb-2 license-plate-frame">
                                        {license}
                                      </span>
                                    );
                                  })}
                                </div>
                                {item._allowedDelete && (
                                  <div className="table-cell py-4 license-plate-btn-row">
                                    <Button
                                      type="button"
                                      variant="light-danger"
                                      size="sm"
                                      style={{ textTransform: "none" }}
                                      onClick={() => vm.removeBatch(item)}
                                      iconLeftVariant="light-danger"
                                      iconLeft={IconType.Cross}>
                                      {vm.translate("removeBatch")}
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                </div>
              </>
            ) : (
              <>
                <Form.Row className="d-flex flex-row flex-wrap align-items-center justify-content-between mx-0 my-0 mb-3 p-s-0 px-lg-7">
                  <h3>{vm.translate("recap")}</h3>
                  <Button
                    type="button"
                    variant="light-danger"
                    size="sm"
                    style={{ textTransform: "none" }}
                    onClick={vm.resetChargeOrder}
                    iconLeftVariant="light-danger"
                    iconLeft={IconType.Cross}>
                    {vm.translate("cancel")}
                  </Button>
                </Form.Row>
                <Col className="d-flex d-flex flex-column align-items-center justify-content-center pt-4 pt-lg-4 pt-xl-4">
                  <Col sm={1} xl={1} className="p-2">
                    <LoadingPane />
                  </Col>
                  <Col sm={4} xl={4} className="d-flex align-items-center justify-content-center p-2">
                    <div className="d-flex align-items-center justify-content-center">{vm.translate("chargeInstructions")}</div>
                  </Col>
                </Col>
              </>
            )}
          </Jumbotron>
        </Col>
        <Col sm={12} lg={4}>
          <View vm={vm.summaryViewModel} />
        </Col>
      </Row>
    </div>
  );
});

registerView(orderRecapView, OrderRecapViewModel);
